import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, EMPTY, Observable, throwError } from 'rxjs';
import { GlobalService } from '../services/global.service';
import { SnackBarService } from '@core/services/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../services/auth.service';
import { isCentralizedAuth } from '@configs/config-data.type';
import { config } from '@configs/config';
import { STORAGE_KEYS } from '@environments/storage.constants';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private isLoggingOut = false;

    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private refreshCoreTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    constructor(
        private router: Router,
        private globalService: GlobalService,
        private snackbarService: SnackBarService,
        private translateService: TranslateService,
        private authService: AuthService,
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const token = GlobalService.getItemFromStorage(STORAGE_KEYS.token);
        const coreToken = GlobalService.getItemFromStorage(STORAGE_KEYS.coreToken);

        let authReq: HttpRequest<any>;
        const originalUrl = req.url;

        if (originalUrl.indexOf('/api/') === 0) {
            const apiUrl = this.globalService.getApiUrl();
            if (!apiUrl) {
                return EMPTY;
            }
            req = req.clone({ url: apiUrl + req.url });
        }

        if (this.isAuthRoute(originalUrl)) {
            authReq = req.clone();
        } else if (this.isAuthenticatedApiCall(originalUrl)) {
            const authHeader = 'Bearer ' + token;
            authReq = req.clone({ setHeaders: { Authorization: authHeader } });
        } else if (this.isAuthenticatedCoreCall(originalUrl)) {
            const coreAuthHeader = 'Bearer ' + coreToken;
            const headers = { 'hospital-authorization': coreAuthHeader };
            authReq = req.clone({ setHeaders: headers });
        } else {
            authReq = req.clone();
        }

        return next
            .handle(authReq)
            .pipe(
                catchError(err => {
                    if (err instanceof HttpErrorResponse && err.status === 401) {
                        if (this.isAuthRoute(originalUrl)) {
                            this.logoutAndNavigateToLogin();
                            return throwError(err.error);
                        }
                        return this.handle401Error(authReq, next, originalUrl);
                    } else if (err.status === 401) {
                        this.logoutAndNavigateToLogin();
                    } else if (err.status === 403) {
                        this.snackbarService.error(this.translateService.instant('snackbar.forbidden'));
                        this.logoutAndNavigateToLogin();
                    } else if (err.status === 404) {
                        this.router.navigateByUrl('404');
                    }

                    return throwError(err.error);
                })
            );
    }

    private isAuthenticatedApiCall(url: string): boolean {
        const user = this.globalService.getUserDetails();
        const token = GlobalService.getItemFromStorage(STORAGE_KEYS.token);

        return user && token && url.indexOf('/api/') === 0;
    }

    private isAuthenticatedCoreCall(url: string): boolean {
        const user = this.globalService.getUserDetails();
        const coreToken = GlobalService.getItemFromStorage(STORAGE_KEYS.coreToken);

        return user && coreToken && isCentralizedAuth(config) && url.indexOf(`${config.coreApiUrl}/api/`) === 0;
    }

    private isAuthRoute(url: string): boolean {
        return url.includes('/api/auth/login') || url.includes('/api/auth/refresh') || url.includes('/api/auth/logout');
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler, originalUrl: string): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);
            this.refreshCoreTokenSubject.next(null);

            return this.authService.refreshToken().pipe(
                switchMap((resp: any) => {
                    this.isRefreshing = false;

                    const newToken = resp.token;
                    const newCoreToken = resp.coreToken;

                    this.refreshTokenSubject.next(newToken);
                    this.refreshCoreTokenSubject.next(newCoreToken);

                    if (this.isAuthenticatedApiCall(originalUrl)) {
                        return next.handle(
                            request.clone({
                                setHeaders: { Authorization: 'Bearer ' + newToken }
                            })
                        );
                    } else if (this.isAuthenticatedCoreCall(originalUrl)) {
                        return next.handle(
                            request.clone({
                                setHeaders: { 'hospital-authorization': 'Bearer ' + newCoreToken }
                            })
                        );
                    }
                }),
                catchError(err => {
                    this.isRefreshing = false;
                    this.logoutAndNavigateToLogin();
                    return throwError(err);
                }),
            );
        } else {
            return combineLatest([
                this.refreshTokenSubject,
                this.refreshCoreTokenSubject,
            ]).pipe(
                filter(([newToken, newCoreToken]) => newToken !== null && newCoreToken !== null),
                take(1),
                switchMap(([newToken, newCoreToken]) => {
                    if (this.isAuthenticatedApiCall(originalUrl)) {
                        return next.handle(
                            request.clone({
                                setHeaders: { Authorization: 'Bearer ' + newToken }
                            })
                        );
                    } else if (this.isAuthenticatedCoreCall(originalUrl)) {
                        return next.handle(
                            request.clone({
                                setHeaders: { 'hospital-authorization': 'Bearer ' + newCoreToken }
                            })
                        );
                    }
                })
            );
        }
    }

    private logoutAndNavigateToLogin() {
        if (this.isLoggingOut) {
            this.router.navigateByUrl('login');
            return;
        }

        this.isLoggingOut = true;
        this.authService.logout().pipe(take(1)).subscribe({
            next: () => {
                this.router.navigateByUrl('login');
            },
            error: () => {
                this.router.navigateByUrl('login');
            },
        });
    }
}
